/**
 * The airframe register: what the unit owns, and what it can launch now.
 *
 * The headline is the readiness — available over total — because owned is not
 * flyable. The rule the server enforces is strict: only a serviceable airframe
 * counts, and maintenance, grounded, and unset all read as unavailable. This
 * panel shows that gap and lets an operator move an airframe between states,
 * which is the common act: marking one down for a fault, or back up after.
 *
 * Each change is its own write. This panel used to save the whole register on
 * every click, which was safe while only people added rows; now aircraft add
 * themselves when they connect, and a panel loaded a minute earlier would have
 * struck off whatever arrived since. Simulated airframes are left out: they
 * are somebody's rehearsal, not something the unit can launch.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  catalogueAirframe,
  getInventory,
  removeAirframe,
  updateAirframe,
  type InventoryItem,
  type InventoryStatus,
} from '@/api/inventory';
import { useHasRole } from '@/stores/authStore';

const OK = 'oklch(0.78 0.15 155)';
const WARN = 'oklch(0.80 0.15 85)';
const DANGER = 'oklch(0.72 0.19 28)';
const MUTED = 'rgba(244,244,245,0.5)';
const WHITE = '#f4f4f5';
const CARD = 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))';

const STATUS_TONE: Record<InventoryStatus, string> = {
  serviceable: OK,
  maintenance: WARN,
  grounded: DANGER,
  unknown: MUTED,
};

/** The states an operator cycles a row through, in order. */
const STATUS_CYCLE: InventoryStatus[] = ['serviceable', 'maintenance', 'grounded', 'unknown'];

function nextStatus(status: InventoryStatus): InventoryStatus {
  const index = STATUS_CYCLE.indexOf(status);
  return STATUS_CYCLE[(index + 1) % STATUS_CYCLE.length]!;
}

export function InventoryPanel() {
  const canEdit = useHasRole('operator');
  const [items, setItems] = useState<InventoryItem[] | null>(null);
  const [tail, setTail] = useState('');

  const load = useCallback(() => {
    void getInventory().then((result) => {
      if (result.ok) setItems(result.data.items.filter((i) => !i.simulated));
    });
  }, []);

  useEffect(load, [load]);

  // Shown at once, then put right by the server's answer, so a write that
  // failed does not leave the screen claiming a status the register lacks.
  const settle = useCallback(
    (write: Promise<{ ok: boolean }>) => {
      void write.then(load);
    },
    [load],
  );

  if (items === null) return null;

  const available = items.filter((i) => i.status === 'serviceable').length;
  const tone = available === 0 && items.length > 0 ? DANGER : available < items.length ? WARN : OK;

  const add = () => {
    const name = tail.trim();
    if (!name) return;
    settle(
      catalogueAirframe({
        tailNumber: name,
        kind: 'copter',
        platform: '',
        manufacturer: '',
        serialNumber: '',
        notes: '',
        components: [],
        status: 'serviceable',
      }),
    );
    setTail('');
  };

  const cycle = (item: InventoryItem) => {
    const status = nextStatus(item.status);
    setItems(items.map((i) => (i.itemId === item.itemId ? { ...i, status } : i)));
    settle(updateAirframe(item.itemId, { status }));
  };

  // An airframe that ever connected is retired rather than deleted, and drops
  // off this list either way.
  const remove = (itemId: string) => {
    setItems(items.filter((i) => i.itemId !== itemId));
    settle(removeAirframe(itemId));
  };

  return (
    <div
      className="rounded-[18px] border p-[16px_18px]"
      style={{ background: CARD, borderColor: 'rgba(255,255,255,0.08)' }}
    >
      <div className="mb-3 flex items-baseline gap-2">
        <span className="label-mono text-[9px]" style={{ color: MUTED, letterSpacing: '0.14em' }}>
          AIRFRAME REGISTER
        </span>
        <span className="tnum ml-auto text-[15px] font-semibold" style={{ color: tone }}>
          {available}
          <span className="text-[11px]" style={{ color: MUTED }}>
            {' '}
            / {items.length} FLYABLE
          </span>
        </span>
      </div>

      {items.length === 0 ? (
        <p className="text-[11px]" style={{ color: MUTED }}>
          No airframes on the register.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {items.map((item) => (
            <div key={item.itemId} className="flex items-center gap-2 text-[12px]">
              <span className="tnum" style={{ color: WHITE, minWidth: 74 }}>
                {item.name}
              </span>
              <span className="label-mono text-[8px]" style={{ color: MUTED }}>
                {item.kind.toUpperCase()}
              </span>
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => cycle(item)}
                title={canEdit ? 'Cycle status' : undefined}
                className="label-mono ml-auto rounded-[6px] px-2 py-0.5 text-[8px] disabled:opacity-70"
                style={{
                  color: STATUS_TONE[item.status],
                  background: `color-mix(in oklch, ${STATUS_TONE[item.status]} 16%, transparent)`,
                }}
              >
                {item.status.toUpperCase()}
              </button>
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => remove(item.itemId)}
                  className="label-mono text-[8px]"
                  style={{ color: MUTED }}
                  aria-label={`Remove ${item.name}`}
                >
                  ✕
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {canEdit ? (
        <div className="mt-3 flex items-center gap-1.5">
          <input
            value={tail}
            onChange={(event) => setTail(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') add();
            }}
            placeholder="tail number"
            className="well flex-1 rounded-[7px] px-2 py-1 font-mono text-[11px] outline-none"
          />
          <button
            type="button"
            onClick={add}
            className="well rounded-[99px] px-3 py-1 label-mono text-[9px]"
          >
            ADD
          </button>
        </div>
      ) : null}
    </div>
  );
}
